import {
    Editor,
    Element,
    Range,
    Transforms,
    type MathInlineElement,
    type Node,
} from "slate";

const isInlineMathElement = (n: Node) =>
    !Editor.isEditor(n) && Element.isElement(n) && n.type === "math-inline";

const isBlockMathElement = (n: Node) =>
    !Editor.isEditor(n) && Element.isElement(n) && n.type === "math-block";

/**
 * Updates the TeX for the inline math element at current selection.
 */
export const updateInlineMathNode = (editor: Editor, newTeX: string) => {
    Transforms.setNodes(editor, {tex: newTeX}, {match: isInlineMathElement});
};

/**
 * Updates the TeX for the block math element at current selection.
 */
export const updateBlockMathNode = (editor: Editor, newTeX: string) => {
    Transforms.setNodes(editor, {tex: newTeX}, {match: isBlockMathElement});
};

/**
 * Gets the math-inline node at the current selection, if any.
 */
export const getInlineMathNode = (
    editor: Editor,
): MathInlineElement | undefined => {
    const {selection} = editor;
    if (!selection) {
        return;
    }

    const [match] = Array.from(
        Editor.nodes<MathInlineElement>(editor, {
            at: Editor.unhangRange(editor, selection),
            match: isInlineMathElement,
        }),
    );

    if (match) {
        return match[0];
    }
};

/**
 * "Unwrap" the math element at the current selection, if there is one.
 * Math inlines will be replaced with text spans containing the raw TeX.
 */
export const unwrapInlineMathNode = (editor: Editor) => {
    const {selection} = editor;
    if (!selection) {
        return;
    }

    const [match] = Array.from(
        Editor.nodes<MathInlineElement>(editor, {
            at: Editor.unhangRange(editor, selection),
            match: isInlineMathElement,
        }),
    );

    if (!match) {
        return;
    }

    const [node, path] = match;

    // Remove the math node...
    Transforms.removeNodes(editor, {at: path});

    // And replace it with text containing the raw TeX content:
    Transforms.insertNodes(
        editor,
        {text: node.tex},
        {at: editor.selection || undefined},
    );
};

export const wrapInlineMathNode = (editor: Editor) => {
    const {selection} = editor;
    if (!selection || Range.isCollapsed(selection)) {
        return;
    }

    const blockNodeIterator = Editor.nodes(editor, {
        at: selection,
        match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            Editor.isBlock(editor, n),
    });

    const blocks = [...blockNodeIterator];

    if (blocks.length === 1) {
        const nodeText = Editor.string(editor, selection);

        Transforms.wrapNodes(
            editor,
            {
                type: "math-inline",
                tex: nodeText,
                children: [{text: nodeText}],
            },
            {split: true},
        );
    }
};

export const isMathInlineSelected = (editor: Editor) => {
    const {selection} = editor;
    if (!selection || Range.isExpanded(selection)) {
        return false;
    }

    const nodes = Editor.nodes(editor, {
        at: selection,
        match: isInlineMathElement,
    });

    return [...nodes].length > 0;
};
